import { environment } from '../../../environments/environment';
import type { TokenStore } from './token-store';

const WEB_REFRESH_STORAGE_KEY = 'lance.web.refresh';

/**
 * PWA implementation. Access token lives strictly in memory (cleared on full reload).
 * Refresh token is primarily server-owned via HttpOnly cookie.
 * In production, refresh token also stays exclusively in memory to prevent XSS exfiltration
 * per LGPD / security best practices.
 * In development, a sessionStorage fallback is supported so local dev / tunnel setups
 * that block cross-origin cookies can refresh on reload.
 */
export class WebTokenStore implements TokenStore {
  private accessToken: string | null = null;
  private memoryRefreshToken: string | null = null;

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  async getRefreshToken(): Promise<string | null> {
    if (environment.production) {
      return this.memoryRefreshToken;
    }
    try {
      return typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(WEB_REFRESH_STORAGE_KEY)
        : this.memoryRefreshToken;
    } catch {
      return this.memoryRefreshToken;
    }
  }

  async setRefreshToken(token: string | null): Promise<void> {
    this.memoryRefreshToken = token;
    if (environment.production) return;

    try {
      if (typeof sessionStorage === 'undefined') return;
      if (token) {
        sessionStorage.setItem(WEB_REFRESH_STORAGE_KEY, token);
      } else {
        sessionStorage.removeItem(WEB_REFRESH_STORAGE_KEY);
      }
    } catch {
      // Ignore storage quota / privacy mode errors.
    }
  }

  async clear(): Promise<void> {
    this.accessToken = null;
    this.memoryRefreshToken = null;
    await this.setRefreshToken(null);
  }
}
